// MyTicketsPage.jsx
import { useEffect, useState } from 'react';
import { Badge, Button, Card, Container } from 'react-bootstrap';
import {
    FaArrowLeft,
    FaCheckCircle,
    FaClock,
    FaComments,
    FaExclamationCircle,
    FaTicketAlt,
    FaTimesCircle,
    FaTrash
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import '../../../../../assets/css/customer/subscreens/account/MyTickets.css';
import BottomNavigation from '../../../../../components/general/bottomnavigation/BottomNavigation';

const MyTicketsPage = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);

  const [filterStatus, setFilterStatus] = useState('All');

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = () => {
    try {
      const savedTickets = JSON.parse(localStorage.getItem('supportTickets') || '[]');
      setTickets(savedTickets);
    } catch (error) {
      console.error('Error loading tickets:', error);
      setTickets([]);
    }
  };

  // Status configuration object
  const STATUS_CONFIG = {
    open: {
      icon: FaClock,
      className: 'status-open'
    },
    resolved: {
      icon: FaCheckCircle,
      className: 'status-resolved'
    },
    closed: {
      icon: FaTimesCircle,
      className: 'status-closed'
    },
    default: {
      icon: FaExclamationCircle,
      className: 'status-default'
    }
  };

  const getStatusConfig = (status) => {
    const normalizedStatus = status?.toLowerCase() || 'default';
    return STATUS_CONFIG[normalizedStatus] || STATUS_CONFIG.default;
  };

  const getStatusIcon = (status) => {
    const { icon: IconComponent } = getStatusConfig(status);
    return <IconComponent className="status-icon" />;
  };

  const getStatusClass = (status) => {
    const { className } = getStatusConfig(status);
    return className;
  };



  const handleDeleteTicket = (ticketNumber) => {
    Swal.fire({
      title: 'Delete Ticket?',
      text: "Are you sure you want to delete this ticket? This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#FF8559',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel',
      customClass: {
        popup: 'delete-ticket-popup',
        title: 'delete-ticket-title',
        confirmButton: 'delete-confirm-button',
        cancelButton: 'delete-cancel-button'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        try {
          const updatedTickets = tickets.filter(t => t.ticketNumber !== ticketNumber);
          localStorage.setItem('supportTickets', JSON.stringify(updatedTickets));
          setTickets(updatedTickets);
          
          Swal.fire({
            icon: 'success',
            title: 'Deleted!',
            text: 'Ticket has been deleted successfully.',
            confirmButtonColor: '#FF8559',
            timer: 2000,
            showConfirmButton: false
          });
        } catch (error) {
          console.error('Error deleting ticket:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to delete ticket. Please try again.',
            confirmButtonColor: '#FF8559'
          });
        }
      }
    });
  };

  const filteredTickets = filterStatus === 'All' 
    ? tickets 
    : tickets.filter(ticket => ticket.status.toLowerCase() === filterStatus.toLowerCase());

  return (
    <div className="my-tickets-page">
      <Container>
        <div className="tickets-shell-container">
          {/* Header */}
          <div className="tickets-header">
            <button className="back-btn" onClick={() => navigate('/support')}>
              <FaArrowLeft size={20} />
            </button>
            <h1>My Tickets</h1>
            <div></div>
          </div>

          {/* Filter Tabs */}
          <div className="filter-section">
            <div className="filter-tabs">
              {['All', 'Open', 'Resolved', 'Closed'].map(status => (
                <button
                  key={status}
                  className={`filter-tab ${filterStatus === status ? 'active' : ''}`}
                  onClick={() => setFilterStatus(status)}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>

          {/* Tickets List */}
          <div className="tickets-list-section">
            {filteredTickets.length === 0 ? (
              <div className="empty-state">
                <FaTicketAlt className="empty-icon" />
                <h3>No Tickets Found</h3>
                <p>
                  {filterStatus === 'All' 
                    ? "You haven't created any support tickets yet." 
                    : `No ${filterStatus.toLowerCase()} tickets found.`}
                </p>
                <Button 
                  className="create-ticket-btn"
                  onClick={() => navigate('/support')}
                >
                  Create New Ticket
                </Button>
              </div>
            ) : (
              <div className="tickets-grid">
                {filteredTickets.map((ticket, index) => (
                  <Card key={index} className="ticket-card">
                    <div className="ticket-card-header">
                      <div className="ticket-number-badge">
                        <FaTicketAlt className="ticket-badge-icon" />
                        <span>{ticket.ticketNumber}</span>
                      </div>
                      <div className="ticket-header-actions">
                        <Badge className={`status-badge ${getStatusClass(ticket.status)}`}>
                          {getStatusIcon(ticket.status)}
                          {ticket.status}
                        </Badge>
                        <button 
                          className="icon-action-btn chat-icon-btn"
                          onClick={() => alert('Chat feature coming soon!')}
                          title="Chat"
                        >
                          <FaComments />
                        </button>
                        <button 
                          className="icon-action-btn delete-icon-btn"
                          onClick={() => handleDeleteTicket(ticket.ticketNumber)}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>

                    <div className="ticket-card-body">
                      <h4 className="ticket-category">{ticket.category}</h4>
                      
                      <p className="ticket-message-preview">
                        {ticket.message.length > 150 
                          ? `${ticket.message.substring(0, 150)}...` 
                          : ticket.message}
                      </p>

                      <div className="ticket-meta">
                        <div className="meta-item">
                          <span className="meta-label">Date:</span>
                          <span className="meta-value">{ticket.date}</span>
                        </div>
                        <div className="meta-divider">•</div>
                        <div className="meta-item">
                          <span className="meta-label">Time:</span>
                          <span className="meta-value">{ticket.time}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
        <BottomNavigation />
      </Container>

    
    </div>
  );
};

export default MyTicketsPage;